import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    TextField,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TableSortLabel,
} from "@mui/material";
import { BookingDataContext } from "../useBookingData";

const API_BASE_URL = "/api2.0/canwebsitev3/";
const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "long",
        year: "numeric",
        timeZone: "UTC",
    });
};

const BookingsList = () => {
    const {
        bookingsWithTimes,
        availableTimes,
        editBooking,
        deleteBooking,
        fetchAvailableTimes,
    } = useContext(BookingDataContext);

    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [currentBooking, setCurrentBooking] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [sortField, setSortField] = useState("Date");
    const [sortOrder, setSortOrder] = useState("ASC");

    const handleEditClick = (booking) => {
        fetchAvailableTimes();
        setCurrentBooking(booking);
        setEditDialogOpen(true);
    };

    const handleSaveEdit = (updatedBooking) => {
        editBooking(currentBooking.BookingId, updatedBooking);
        setEditDialogOpen(false);
    };

    if (isLoading) {
        return <div>Loading...</div>;
    }
    console.log("BOOKING", bookingsWithTimes);

    const handleSortChange = (field) => {
        if (sortField === field) {
            setSortOrder(sortOrder === "ASC" ? "DESC" : "ASC");
        } else {
            setSortField(field);
            setSortOrder("ASC");
        }
    };

    const getComparator = () => {
        return (a, b) => {
            const valueA = a[sortField];
            const valueB = b[sortField];
            if (sortField === "Date" || sortField.includes("Time")) {
                return sortOrder === "ASC"
                    ? new Date(valueA) - new Date(valueB)
                    : new Date(valueB) - new Date(valueA);
            } else {
                if (valueA < valueB) return sortOrder === "ASC" ? -1 : 1;
                if (valueA > valueB) return sortOrder === "ASC" ? 1 : -1;
                return 0;
            }
        };
    };

    const sortedBookings = bookingsWithTimes.sort(getComparator());
    return (
        <TableContainer component={Paper} sx={{ mt: 4 }}>
            <Typography variant="h6" sx={{ p: 2 }}>
                All Bookings
            </Typography>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell>
                            <TableSortLabel
                                active={sortField === "UserEmail"}
                                direction={
                                    sortField === "UserEmail"
                                        ? sortOrder.toLowerCase()
                                        : "asc"
                                }
                                onClick={() => handleSortChange("UserEmail")}
                            >
                                User Email
                            </TableSortLabel>
                        </TableCell>
                        <TableCell align="right">
                            <TableSortLabel
                                active={sortField === "Date"}
                                direction={
                                    sortField === "Date"
                                        ? sortOrder.toLowerCase()
                                        : "asc"
                                }
                                onClick={() => handleSortChange("Date")}
                            >
                                Date
                            </TableSortLabel>
                        </TableCell>
                        <TableCell align="right">
                            <TableSortLabel
                                active={sortField === "start_time"}
                                direction={
                                    sortField === "start_time"
                                        ? sortOrder.toLowerCase()
                                        : "asc"
                                }
                                onClick={() => handleSortChange("start_time")}
                            >
                                Start Time
                            </TableSortLabel>
                        </TableCell>
                        <TableCell align="right">
                            <TableSortLabel
                                active={sortField === "end_time"}
                                direction={
                                    sortField === "end_time"
                                        ? sortOrder.toLowerCase()
                                        : "asc"
                                }
                                onClick={() => handleSortChange("end_time")}
                            >
                                End Time
                            </TableSortLabel>
                        </TableCell>
                        {/* <TableCell align="right">
                            <TableSortLabel
                                active={sortField === "Status"}
                                direction={
                                    sortField === "Status"
                                        ? sortOrder.toLowerCase()
                                        : "asc"
                                }
                                onClick={() => handleSortChange("Status")}
                            >
                                Status
                            </TableSortLabel>
                        </TableCell> */}
                        <TableCell align="right">Actions</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {sortedBookings.map((booking) => (
                        <TableRow key={booking.BookingId}>
                            <TableCell>{booking.UserEmail}</TableCell>
                            <TableCell align="right">
                                {formatDate(booking.Date)}
                            </TableCell>
                            <TableCell align="right">
                                {new Date(
                                    `1970-01-01T${booking.start_time}`
                                ).toLocaleTimeString([], {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                    hour12: true,
                                })}
                            </TableCell>
                            <TableCell align="right">
                                {new Date(
                                    `1970-01-01T${booking.end_time}`
                                ).toLocaleTimeString([], {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                    hour12: true,
                                })}
                            </TableCell>
                            <TableCell align="right">
                                {booking.Status}
                            </TableCell>
                            <TableCell align="right">
                                <Button
                                    onClick={() => handleEditClick(booking)}
                                >
                                    Edit
                                </Button>
                                <Button
                                    onClick={() =>
                                        deleteBooking(booking.BookingId)
                                    }
                                >
                                    Delete
                                </Button>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            {currentBooking && (
                <EditBookingDialog
                    booking={currentBooking}
                    availableTimes={availableTimes} // Pass availableTimes to the dialog
                    open={editDialogOpen}
                    onClose={() => setEditDialogOpen(false)}
                    onSave={handleSaveEdit}
                />
            )}
        </TableContainer>
    );
};

// EditBookingDialog Component
const EditBookingDialog = ({
    booking,
    availableTimes,
    open,
    onClose,
    onSave,
}) => {
    const [userEmail, setUserEmail] = useState(booking.UserEmail || "");
    const [selectedTimeId, setSelectedTimeId] = useState("");
    const [status, setStatus] = useState(booking.Status || "");

    const { fetchBookingsWithAvailableTimes, fetchAvailableTimes } =
        useContext(BookingDataContext);

    useEffect(() => {
        setUserEmail(booking.UserEmail || "");
        setSelectedTimeId(booking.AvailableTimeId || "");
        setStatus(booking.Status || "");
    }, [booking]);
    const formatTime = (time) => {
        // Assumes `time` is a string like "14:00:00", and date is the date part of ISO string "YYYY-MM-DD"
        const [hours, minutes] = time.split(":");
        // Create a date object at the beginning of the ECMAScript epoch and set the UTC hours and minutes
        const timeObj = new Date(Date.UTC(1970, 0, 1, hours, minutes));
        return timeObj.toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
            timeZone: "UTC",
        });
    };

    // When saving, find the selected time details to pass back
    const handleSave = async () => {
        const updatedBooking = {
            UserEmail: userEmail,
            AvailableTimeId: selectedTimeId,
            Status: status,
        };

        try {
            const response = await axios.put(
                `${API_BASE_URL}/updatebooking/${booking.BookingId}`,
                updatedBooking
            );
            if (response.status === 200) {
                onClose(); // Close the dialog
                fetchAvailableTimes();
                await fetchBookingsWithAvailableTimes(); // Refresh bookings list
                alert("Booking updated successfully.");
            } else {
                alert("Failed to update booking.");
            }
        } catch (error) {
            console.error("Failed to update booking:", error);
            alert("Failed to update booking.");
        }
    };

    return (
        <Dialog open={open} onClose={onClose}>
            <DialogTitle>Edit Booking</DialogTitle>
            <DialogContent>
                <TextField
                    margin="dense"
                    label="User Email"
                    type="email"
                    fullWidth
                    value={userEmail}
                    onChange={(e) => setUserEmail(e.target.value)}
                />
                <FormControl fullWidth margin="dense">
                    <InputLabel>Available Times</InputLabel>
                    <Select
                        value={selectedTimeId}
                        onChange={(e) => setSelectedTimeId(e.target.value)}
                        label="Available Times"
                    >
                        {availableTimes.map((time) => (
                            <MenuItem key={time.Id} value={time.Id}>
                                {`${formatDate(time.Date)} ${formatTime(
                                    time.start_time
                                )} - ${formatTime(time.end_time)}`}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <TextField
                    margin="dense"
                    label="Status"
                    fullWidth
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={handleSave}>Save</Button>
            </DialogActions>
        </Dialog>
    );
};

export default BookingsList;
